import type { SettingsRow } from '@bn-games/ui-shell';
import type {
  Difficulty,
  Language,
  Settings,
  ThemeName,
} from '../settings/types.js';
import { t } from '../i18n/I18n.js';

const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard'];
const THEME_NAMES: readonly ThemeName[] = ['mahogany', 'persian', 'deco', 'monte'];
const LANGUAGES: readonly Language[] = ['en', 'ru'];

/** Minimal contract the row builder needs — both `MenuContext` and the
 *  pause-menu adapter (task 19) satisfy this. */
export interface SettingsRowCtx {
  getSettings(): Settings;
  updateSettings(partial: Partial<Settings>): void;
}

export interface BuildSettingsRowsOptions {
  readonly ctx: SettingsRowCtx;
  /** Pause-menu wraps game-config (difficulty) changes with confirm + restart. */
  readonly onGameConfigChange?: (apply: () => void) => void;
  /** Disable game-config rows entirely (e.g. mid-solve). */
  readonly disableGameConfig?: boolean;
}

export function buildSettingsRows(opts: BuildSettingsRowsOptions): readonly SettingsRow[] {
  const { ctx, onGameConfigChange, disableGameConfig = false } = opts;
  const s = ctx.getSettings();

  const applyGameConfig = (partial: Partial<Settings>): void => {
    const apply = (): void => ctx.updateSettings(partial);
    if (onGameConfigChange) onGameConfigChange(apply);
    else apply();
  };

  return [
    {
      kind: 'selector',
      key: 'difficulty',
      label: t('settings.difficulty'),
      value: s.difficulty,
      options: DIFFICULTIES,
      format: (v) => t(`settings.difficulties.${v as Difficulty}`),
      onChange: (next) => applyGameConfig({ difficulty: next as Difficulty }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'toggle',
      key: 'highlightErrors',
      label: t('settings.highlightErrors'),
      value: s.highlightErrors,
      onClick: () => ctx.updateSettings({ highlightErrors: !s.highlightErrors }),
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'theme',
      label: t('settings.theme'),
      value: s.theme,
      options: THEME_NAMES,
      format: (v) => t(`settings.themes.${v as ThemeName}`),
      onChange: (next) => ctx.updateSettings({ theme: next as ThemeName }),
      tab: 'interface',
    },
    {
      kind: 'selector',
      key: 'language',
      label: t('settings.language'),
      value: s.language,
      options: LANGUAGES,
      format: (v) => t(`settings.languages.${v as Language}`),
      onChange: (next) => ctx.updateSettings({ language: next as Language }),
      tab: 'interface',
    },
  ];
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const PAD_WIDTH = 2;

/** Format a duration in ms as `mm:ss` (zero-padded, clamped at zero). */
export function formatTime(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / MS_PER_SECOND));
  const min = Math.floor(totalSec / SECONDS_PER_MINUTE);
  const sec = totalSec % SECONDS_PER_MINUTE;
  return `${String(min).padStart(PAD_WIDTH, '0')}:${String(sec).padStart(PAD_WIDTH, '0')}`;
}
